import React from "react";
// icons
import { FaUser } from "react-icons/fa";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { renderCallStatusIcon } from "../../utils/renderCallStatusIcon";

const SessionSidebar = ({
  headers,
  sessionsPerPage,
  selectedId,
  onSelectId,
  totalSessions,
  currentPage,
  onSetCurrentPage,
  startIndex,
  endIndex,
}) => {
  const totalPages = Math.ceil(totalSessions / sessionsPerPage);

  const handleSelect = (sessionId) => {
    // Re-clicking the active session is a no-op; selection is never toggled off.
    if (sessionId !== selectedId) {
      onSelectId(sessionId);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      onSetCurrentPage(newPage);
    }
  };

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    const time = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
    const dateStr = date.toLocaleDateString("en-US", {
      month: "numeric",
      day: "numeric",
    });
    return `${dateStr} ${time}`;
  };

  const getHeaderName = (hdr) => {
    if (typeof hdr.name === "string") {
      return hdr.name;
    }
    if (hdr.name && hdr.name.title) {
      return hdr.name.title;
    }
    return "<Untitled>";
  };

  return (
    <div className="flex flex-col h-full text-white relative">
      <div className="flex-shrink-0">
        <div className="text-xs text-gray-400 text-right p-4">
          {totalSessions} sessions
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex-shrink-0 bg-gray-800 border-t border-gray-700 p-3 w-full">
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-400">
              {startIndex + 1}-{endIndex} of {totalSessions} sessions
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-1 rounded ${
                  currentPage === 1
                    ? "text-gray-500 cursor-not-allowed"
                    : "text-blue-400 hover:bg-gray-700"
                }`}
                aria-label="Previous page"
              >
                <FaChevronLeft size={14} />
              </button>
              <span className="text-xs px-2">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-1 rounded ${
                  currentPage === totalPages
                    ? "text-gray-500 cursor-not-allowed"
                    : "text-blue-400 hover:bg-gray-700"
                }`}
                aria-label="Next page"
              >
                <FaChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={`flex-grow overflow-y-auto dark-slim-scrollbar pb-16`}>
        {headers.length === 0 ? (
          <div className="p-4 text-center text-gray-400">No sessions found</div>
        ) : (
          headers.map((hdr) => (
            <div
              key={hdr.session_id}
              className={`border-b border-b-gray-800 p-3 cursor-pointer ${
                hdr.session_id === selectedId
                  ? "bg-blue-600 bg-opacity-50"
                  : "hover:bg-gray-800 hover:bg-opacity-50"
              }`}
              onClick={() => handleSelect(hdr.session_id)}
            >
              <div className="flex justify-between items-start">
                <span className="text-xs text-gray-400 text-ellipsis truncate">
                  {formatDateTime(new Date(hdr.created_at))}
                </span>
                <div className="flex items-center space-x-2">
                  <span
                    className={`text-xs text-gray-500 ${
                      hdr.modality === "chat" ? "hidden" : ""
                    }`}
                  >
                    {hdr.call_direction === "inbound" ? "In" : "Out"}
                  </span>
                  {renderCallStatusIcon(hdr)}
                </div>
              </div>
              <div className="mt-2 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-white font-medium">
                    {getHeaderName(hdr)}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  {hdr.created_by && (
                    <div className="flex items-center text-xs text-gray-400 mt-1 text-ellipsis truncate">
                      <FaUser className="mr-1 text-xs" />
                      <span className="truncate">{hdr.created_by}</span>
                    </div>
                  )}
                  <div className="text-xs text-gray-400 mt-1 text-ellipsis truncate">
                    {hdr.direction === "inbound" ? hdr.from : hdr.to}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SessionSidebar;
